import { useParams } from 'react-router';
// Icons used ONLY by commented-out nav items (Sparkles / BarChart3 / Bell) are not
// imported — an unused import is a type error here. Re-add one alongside its item
// when re-enabling. Truck stays: the live Pickup Portal uses it.
import {
  LayoutDashboard, UtensilsCrossed, Store, Users, CreditCard, QrCode,
  CalendarDays, ListOrdered, Tag, ShoppingBag, Star, LayoutGrid,
  Wallet, Heart, ChefHat, Wine, TrendingUp, Languages,
  Settings, Truck, UserCog, ClipboardList, Package,
  Receipt, FileText, UserCircle, Link2, Images, BarChart3,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useOrderNotifications } from '@/contexts/OrderNotificationsContext';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import type { Restaurant, PlanTier } from '@gatheredhq/types';
import { hasPaymentProcessing, planAllowsOrdering, meetsMinPlan, FEATURE_REGISTRY } from '@gatheredhq/types';


export interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  locked?: boolean;
  planGated?: boolean;
  minPlan?: string;
  dividerBefore?: boolean;
  badge?: number;
  ownerOnly?: boolean;
  group?: string;
  permissionKey?: string;
}

export interface UseNavItemsResult {
  navItems: NavItem[];
  visibleItems: NavItem[];
  activeItems: NavItem[];
  lockedItems: NavItem[];
  isItemLocked: (item: NavItem) => boolean;
  currentVenue: Restaurant | null;
  isFree: boolean;
  /**
   * The plan itself, not a rung number.
   *
   * It was `userPlanLevel: number`, read out of a local ladder copy that had no `hotel`
   * — so Hotel scored 0 and every consumer of this number treated a paying Hotel venue
   * as Free. Handing out the tier and letting callers use `meetsMinPlan` removes the
   * chance to mis-rank one.
   */
  userPlan: PlanTier;
  extraLocationCount: number;
}

export function useNavItems(): UseNavItemsResult {
  const { slug: venueSlug } = useParams();
  const { user, venues } = useAuth();

  if (venueSlug) sessionStorage.setItem('ghq_active_venue', venueSlug);
  const rememberedSlug = venueSlug ?? sessionStorage.getItem('ghq_active_venue') ?? undefined;
  const currentVenue = venues.find(v => v.slug === rememberedSlug) ?? venues[0] ?? null;

  const isFree = user?.plan === 'free';
  /**
   * The SHARED ladder. This file used to keep its own copy, commented "Mirrors
   * PLAN_ORDER in @gatheredhq/types" — and it did not: it had no `hotel`. The lookup is
   * `PLAN_ORDER[plan] ?? 0`, so a Hotel organization scored ZERO and every nav item with
   * a `minPlan` above free vanished. A paying Hotel venue opened the dashboard to a
   * free-plan sidebar.
   *
   * A copy cannot be checked by the compiler — `Record<string, number>` accepts any key
   * and `?? 0` swallows the miss silently. Import it, never mirror it.
   */
  const userPlan = (user?.plan ?? 'free') as PlanTier;
  // Browse-only plans (free, starter) can't access ordering/operations portals.
  const canOrderPlan = planAllowsOrdering((user?.plan ?? 'free') as PlanTier);

  // AppLayout renders both Sidebar and Topbar, and each calls this hook — so this used
  // to be two separate requests for identical bytes on every page load. They now share
  // one cache entry.
  //
  // No `enabled` guard here on purpose. A previous `enabled: !isFree` looked like it
  // spared free orgs the request but did nothing: react-query fetches a key when ANY
  // mounted observer is enabled, and AuthContext observes this same key with
  // `enabled: !!user`. It would also have been the wrong intent — /api/billing/status
  // answers for free and org-less users too (`plan: 'free'`), and AuthContext depends
  // on that response for `activeAddons`.
  const { data: billing } = useBillingStatus();
  const extraLocationCount = billing?.extraLocationCount ?? 0;

  // WebSocket-fed "new orders" count from OrderNotificationsProvider — shared
  // across Sidebar/Topbar/MobileNav, zero REST.
  const { newOrderCount } = useOrderNotifications();
  const s = currentVenue?.slug ?? '';

  const navItems: NavItem[] = currentVenue
    ? [
      { to: `/venues/${s}/overview`, label: 'Dashboard', icon: <LayoutDashboard className="w-[18px] h-[18px] flex-shrink-0" /> },
      { to: `/venues/${s}/menu`, label: 'Menu Items', icon: <UtensilsCrossed className="w-[18px] h-[18px] flex-shrink-0" />, permissionKey: 'menu' },
      // "Showcase": the page holds story highlights AND What's New banners, so "Stories"
      // named only half of it. Not "Guest Page" — Venue Profile already owns a Guest App
      // tab, and two entries about the guest app in one sidebar is a coin toss for the
      // owner. "Promotions" and "Campaigns" are taken by real features.
      // The ROUTE stays /stories so existing links and bookmarks keep working.
      { to: `/venues/${s}/stories`, label: 'Showcase', icon: <Images className="w-[18px] h-[18px] flex-shrink-0" />, permissionKey: 'stories' },
      { to: `/venues/${s}/inventory`, label: 'Inventory', icon: <Package className="w-[18px] h-[18px] flex-shrink-0" />, minPlan: 'scale', permissionKey: 'inventory' },

      { to: `/venues/${s}/crm`, label: 'Guest CRM', icon: <UserCircle className="w-[18px] h-[18px] flex-shrink-0" />, minPlan: 'scale', dividerBefore: true, group: 'CRM & Loyalty', permissionKey: 'bookings' },
      // Hidden from nav (routes remain URL-reachable) — matches the Delivery Hub / Notifications pattern.
      // { to: `/venues/${s}/loyalty`, label: 'Loyalty', icon: <Star className="w-[18px] h-[18px] flex-shrink-0" />, minPlan: 'scale', group: 'CRM & Loyalty', permissionKey: 'bookings' },
      // { to: `/venues/${s}/campaigns`, label: 'Campaigns', icon: <MessageCircle className="w-[18px] h-[18px] flex-shrink-0" />, minPlan: 'scale', group: 'CRM & Loyalty', permissionKey: 'bookings' },
      // { to: `/venues/${s}/identity`, label: 'Identity', icon: <Shield className="w-[18px] h-[18px] flex-shrink-0" />, minPlan: 'scale', group: 'CRM & Loyalty', permissionKey: 'bookings' },

      { to: `/venues/${s}/bookings`, label: 'Bookings', icon: <CalendarDays className="w-[18px] h-[18px] flex-shrink-0" />, minPlan: 'scale', dividerBefore: true, permissionKey: 'bookings' },
      { to: `/venues/${s}/waitlist`, label: 'Waitlist', icon: <ListOrdered className="w-[18px] h-[18px] flex-shrink-0" />, minPlan: 'scale', permissionKey: 'waitlist' },
      { to: `/venues/${s}/promotions`, label: 'Promotions', icon: <Tag className="w-[18px] h-[18px] flex-shrink-0" />, minPlan: 'scale', permissionKey: 'promotions' },
      // minPlan from the registry, so the sidebar link and the route guard can never
      // disagree — a visible link that redirects to /billing is worse than no link.
      { to: `/venues/${s}/reviews`, label: 'Reviews', icon: <Star className="w-[18px] h-[18px] flex-shrink-0" />, minPlan: FEATURE_REGISTRY.reviews.minPlan, permissionKey: 'reviews' },

      // Only the Orders tab carries a live badge — a WebSocket-fed "new
      // orders since you last opened Orders" notification (no REST). The
      // per-portal count badges were removed: they cost a full order-history
      // download on every page load for numbers few owners watched.
      { to: `/venues/${s}/orders`, label: 'Orders', icon: <ShoppingBag className="w-[18px] h-[18px] flex-shrink-0" />, planGated: true, badge: newOrderCount, dividerBefore: true, permissionKey: 'orders' },
      { to: `/venues/${s}/floor-plan`, label: 'Floor Plan', icon: <LayoutGrid className="w-[18px] h-[18px] flex-shrink-0" />, minPlan: 'operate', permissionKey: 'floor_plan' },
      { to: `/venues/${s}/qr-codes`, label: 'QR Codes', icon: <QrCode className="w-[18px] h-[18px] flex-shrink-0" />, permissionKey: 'qr_codes' },

      { to: `/venues/${s}/cashier`, label: 'Cashier Portal', icon: <Wallet className="w-[18px] h-[18px] flex-shrink-0" />, planGated: true, dividerBefore: true, permissionKey: 'cashier' },
      { to: `/venues/${s}/kitchen`, label: 'Kitchen Portal', icon: <ChefHat className="w-[18px] h-[18px] flex-shrink-0" />, planGated: true, permissionKey: 'kitchen' },
      { to: `/venues/${s}/bar`, label: 'Bar Portal', icon: <Wine className="w-[18px] h-[18px] flex-shrink-0" />, planGated: true, permissionKey: 'bar' },
      { to: `/venues/${s}/waiter`, label: 'Waiter Portal', icon: <ClipboardList className="w-[18px] h-[18px] flex-shrink-0" />, minPlan: 'operate', permissionKey: 'waiter' },
      { to: `/venues/${s}/delivery`, label: 'Pickup Portal', icon: <Truck className="w-[18px] h-[18px] flex-shrink-0" />, planGated: true, permissionKey: 'delivery' },
      // { to: `/venues/${s}/delivery-hub`, label: 'Delivery Hub', icon: <Truck className="w-[18px] h-[18px] flex-shrink-0" />, minPlan: 'scale', permissionKey: 'delivery' },
      { to: `/venues/${s}/staff`, label: 'Staff Portal', icon: <Users className="w-[18px] h-[18px] flex-shrink-0" />, planGated: true, permissionKey: 'staff' },

      { to: `/venues/${s}/analytics`, label: 'Analytics', icon: <BarChart3 className="w-[18px] h-[18px] flex-shrink-0" />, minPlan: 'menu', dividerBefore: true, group: 'Finance', permissionKey: 'analytics' },
      ...((hasPaymentProcessing((user?.plan ?? 'free') as PlanTier, user?.activeAddons))
        ? [{ to: `/venues/${s}/financials`, label: 'Financials', icon: <TrendingUp className="w-[18px] h-[18px] flex-shrink-0" />, group: 'Finance', permissionKey: 'financials' }]
        : [{ to: `/venues/${s}/financials`, label: 'Financials', icon: <TrendingUp className="w-[18px] h-[18px] flex-shrink-0" />, minPlan: 'scale', group: 'Finance', permissionKey: 'financials' }]),
      { to: `/venues/${s}/accounting`, label: 'Accounting', icon: <Receipt className="w-[18px] h-[18px] flex-shrink-0" />, minPlan: 'scale', group: 'Finance', permissionKey: 'financials' },
      { to: `/venues/${s}/z-reports`, label: 'Z-Reports', icon: <FileText className="w-[18px] h-[18px] flex-shrink-0" />, minPlan: 'scale', group: 'Finance', permissionKey: 'financials' },
      { to: `/venues/${s}/tips`, label: 'Tips', icon: <Heart className="w-[18px] h-[18px] flex-shrink-0" />, minPlan: 'operate', group: 'Finance', permissionKey: 'tips' },
      // Hidden from nav (route remains URL-reachable) — matches the Delivery Hub / Notifications pattern.
      // { to: `/venues/${s}/terminals`, label: 'Terminals', icon: <CreditCard className="w-[18px] h-[18px] flex-shrink-0" />, minPlan: 'scale', group: 'Finance', permissionKey: 'financials' },

      { to: `/venues/${s}/venue-profile`, label: 'Venue Profile', icon: <Store className="w-[18px] h-[18px] flex-shrink-0" />, dividerBefore: true },
      { to: `/venues/${s}/integrations`, label: 'Integrations', icon: <Link2 className="w-[18px] h-[18px] flex-shrink-0" />, minPlan: 'menu', permissionKey: 'integrations' },
      { to: '/billing', label: 'Subscriptions', icon: <CreditCard className="w-[18px] h-[18px] flex-shrink-0" />, ownerOnly: true },
      // { to: `/venues/${s}/notifications`, label: 'Notifications', icon: <Bell className="w-[18px] h-[18px] flex-shrink-0" />, minPlan: 'scale', permissionKey: 'notifications' },
      { to: `/venues/${s}/translations`, label: 'Translations', icon: <Languages className="w-[18px] h-[18px] flex-shrink-0" />, minPlan: 'menu', permissionKey: 'translations' },
      // { to: `/venues/${s}/reports`, label: 'Reports & Analytics', icon: <BarChart3 className="w-[18px] h-[18px] flex-shrink-0" />, planGated: true, permissionKey: 'analytics' },
      // Hidden from nav (route remains URL-reachable) — matches the Delivery Hub /
      // Notifications pattern. Closed 2026-07-15 until the token costing is settled:
      // the page publishes a per-action Credit Costs grid (AI_CREDIT_COSTS) that
      // isn't final. Credit consumption and the Billing credit pack are unaffected.
      // { to: '/ai-credits', label: 'AI Credits', icon: <Sparkles className="w-[18px] h-[18px] flex-shrink-0" />, planGated: true, ownerOnly: true },
      { to: '/account/settings', label: 'Settings', icon: <Settings className="w-[18px] h-[18px] flex-shrink-0" />, dividerBefore: true },
      { to: `/venues/${s}/team`, label: 'Managers', icon: <UserCog className="w-[18px] h-[18px] flex-shrink-0" />, minPlan: 'scale', ownerOnly: true },
    ]
    : [];

  // The role AT THIS VENUE, not a single role for the whole session.
  //
  // It used to read `user.role`, which was computed across every venue the person
  // belonged to with `owner` winning. Someone who owned one restaurant and was invited
  // as a restricted manager to another therefore arrived at the second one as an
  // owner: `isManager` was false, so every ownerOnly item showed and the permissions
  // the inviting owner had set were never even read.
  const venueRole = ((currentVenue as unknown as Record<string, unknown>)?._role as string | null | undefined)
    ?? user?.role ?? null;
  const isManager = venueRole === 'manager';
  const userPermissions = isManager ? (currentVenue as unknown as Record<string, unknown>)?._userPermissions as string[] | null | undefined : null;
  const hasCustomPermissions = isManager && Array.isArray(userPermissions);

  const visibleItems = navItems.filter(item => {
    if (isManager && item.ownerOnly) return false;
    if (hasCustomPermissions && item.permissionKey) {
      return userPermissions!.includes(item.permissionKey);
    }
    return true;
  });

  const isItemLocked = (item: NavItem): boolean =>
    !!item.locked
    || (!!item.planGated && !canOrderPlan)
    || (!!item.minPlan && !meetsMinPlan(userPlan, item.minPlan as PlanTier));

  const activeItems = visibleItems.filter(item => !isItemLocked(item));
  const lockedItems = visibleItems.filter(item => isItemLocked(item));

  return {
    navItems,
    visibleItems,
    activeItems,
    lockedItems,
    isItemLocked,
    currentVenue,
    isFree,
    userPlan,
    extraLocationCount,
  };
}
